/**
 * Postings page — ingest and browse job postings.
 *
 * Features:
 *  - "Add posting" button opens JobPostingModal (URL paste → queues bg extraction)
 *  - Polls every 3 seconds while any posting is pending or processing
 *  - Each card shows status badge + extracted title, company, location
 *  - Completed postings expand to show structured requirements
 *  - Duplicate warning banner shown when user arrives after ingesting a duplicate
 *  - Delete per posting with confirmation
 *  - Always-visible navigation: Capabilities / Postings / Resumes
 */
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Trash2,
  ChevronDown,
  ChevronUp,
  Loader2,
  AlertTriangle,
  ExternalLink,
  CheckCircle2,
  XCircle,
  Clock,
  FileText,
  List,
  Send,
  Wand2,
} from 'lucide-react';
import { jobPostingApi, authApi, resumeApi, creditsApi, applicationApi, CREDIT_COSTS, APPLICATION_ACTION_LABELS } from '@/lib/api';
import type { JobPosting, IngestJobPostingResponse, Application, ApplicationAction } from '@/lib/api';
import { MarkAsAppliedModal } from '@/components/MarkAsAppliedModal';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Navigation } from '@/components/Navigation';
import { JobPostingModal } from '@/components/JobPostingModal';
import { CreditGate } from '@/components/CreditGate';
import { showErrorToast } from '@/lib/toast';

// ── Helpers ────────────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function StatusBadge({ status }: { status: JobPosting['status'] }) {
  switch (status) {
    case 'pending':
      return (
        <Badge variant="outline" className="gap-1 text-muted-foreground">
          <Clock className="h-3 w-3" />
          Pending
        </Badge>
      );
    case 'processing':
      return (
        <Badge variant="outline" className="gap-1 text-blue-600 border-blue-300">
          <Loader2 className="h-3 w-3 animate-spin" />
          Extracting…
        </Badge>
      );
    case 'completed':
      return (
        <Badge variant="outline" className="gap-1 text-green-700 border-green-300">
          <CheckCircle2 className="h-3 w-3" />
          Ready
        </Badge>
      );
    case 'failed':
      return (
        <Badge variant="outline" className="gap-1 text-destructive border-destructive/40">
          <XCircle className="h-3 w-3" />
          Failed
        </Badge>
      );
  }
}

// ── Requirement list section ───────────────────────────────────────────────────

function ReqSection({ title, items }: { title: string; items: string[] }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </h4>
      <ul className="space-y-1">
        {items.map((item, i) => (
          <li key={i} className="flex gap-2 text-sm">
            <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/60" />
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── Single posting card ────────────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const actionLabel = (a: ApplicationAction) => APPLICATION_ACTION_LABELS[a]!;

function PostingCard({
  posting,
  application,
  onDelete,
  onGenerate,
  onSynthesize,
  onBuildManually,
  onPasteDescription,
  onMarkApplied,
  creditsBalance = 0,
}: {
  posting: JobPosting;
  application?: Application | null;
  onDelete: (id: string) => void;
  onGenerate: (postingId: string) => void;
  onSynthesize: (postingId: string) => void;
  onBuildManually: (postingId: string) => void;
  onPasteDescription: (posting: JobPosting) => void;
  onMarkApplied: (posting: JobPosting) => void;
  creditsBalance?: number;
}) {
  const [expanded, setExpanded] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const req = posting.requirements;
  const hasReqs =
    (req.mustHaves?.length ?? 0) > 0 ||
    (req.niceToHaves?.length ?? 0) > 0 ||
    (req.responsibilities?.length ?? 0) > 0;

  const displayTitle = posting.title ?? req.role ?? '(Untitled)';
  const displayCompany = posting.hiringCompany ?? '—';

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-3">
          {/* Left: title + meta */}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge status={posting.status} />
              {req.seniority && (
                <Badge variant="secondary" className="text-xs">
                  {req.seniority}
                </Badge>
              )}
            </div>
            <h3 className="mt-1.5 truncate text-base font-semibold leading-snug">
              {displayTitle}
            </h3>
            <p className="text-sm text-muted-foreground">
              {displayCompany}
              {posting.location && ` · ${posting.location}`}
            </p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Added {formatDate(posting.createdAt)}
            </p>
          </div>

          {/* Right: actions */}
          <div className="flex shrink-0 items-center gap-1">
            {posting.status === 'completed' && (
              <>
                <CreditGate cost={CREDIT_COSTS.resumeGeneration} balance={creditsBalance}>
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-1.5 text-xs"
                    onClick={() => onGenerate(posting.id)}
                    title="Generate a tailored resume for this posting"
                  >
                    <FileText className="h-3.5 w-3.5" />
                    Generate Resume
                  </Button>
                </CreditGate>
                <CreditGate cost={CREDIT_COSTS.resumeGeneration} balance={creditsBalance}>
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-1.5 text-xs"
                    onClick={() => onSynthesize(posting.id)}
                    title="Synthesize a resume by rewriting bullets to directly address job requirements"
                  >
                    <Wand2 className="h-3.5 w-3.5" />
                    Synthesize Resume
                  </Button>
                </CreditGate>
                <Button
                  size="sm"
                  variant="ghost"
                  className="gap-1.5 text-xs"
                  onClick={() => onBuildManually(posting.id)}
                  title="Build a resume by selecting capabilities"
                >
                  <List className="h-3.5 w-3.5" />
                  Build Manually
                </Button>
                {application ? (
                  <div className="flex flex-col items-end leading-tight">
                    <span className="text-xs font-medium text-green-700">
                      {actionLabel(application.lastAction)}
                    </span>
                    <span className="text-[11px] text-muted-foreground">
                      {formatDate(application.lastActionDate)}
                    </span>
                  </div>
                ) : (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="gap-1.5 text-xs text-green-700 hover:text-green-800 hover:bg-green-50"
                    onClick={() => onMarkApplied(posting)}
                    title="Record that you applied to this job"
                  >
                    <Send className="h-3.5 w-3.5" />
                    Mark Applied
                  </Button>
                )}
              </>
            )}
            <a
              href={posting.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground"
              title="Open original posting"
            >
              <ExternalLink className="h-4 w-4" />
            </a>

            {!confirmDelete ? (
              <button
                onClick={() => setConfirmDelete(true)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                title="Delete posting"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            ) : (
              <div className="flex items-center gap-1 rounded-md border border-destructive/30 bg-destructive/5 px-2 py-1 text-xs">
                <span className="text-destructive">Delete?</span>
                <button
                  onClick={() => onDelete(posting.id)}
                  className="font-semibold text-destructive hover:underline"
                >
                  Yes
                </button>
                <span className="text-muted-foreground">/</span>
                <button
                  onClick={() => setConfirmDelete(false)}
                  className="text-muted-foreground hover:underline"
                >
                  No
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Error message for failed extractions */}
        {posting.status === 'failed' && (
          <div className="mt-2 space-y-2">
            {posting.errorMessage && (
              <div className="flex items-start gap-2 rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>{posting.errorMessage}</span>
              </div>
            )}
            <Button
              size="sm"
              variant="outline"
              className="h-7 gap-1.5 text-xs"
              onClick={() => onPasteDescription(posting)}
            >
              <FileText className="h-3.5 w-3.5" />
              Paste description
            </Button>
          </div>
        )}
      </CardHeader>

      {/* Expandable requirements section */}
      {posting.status === 'completed' && (
        <CardContent className="pt-0">
          {req.summary && !expanded && (
            <p className="mb-2 text-sm text-muted-foreground line-clamp-2">{req.summary}</p>
          )}

          {expanded && (
            <div className="space-y-4 border-t pt-4">
              {req.summary && (
                <div>
                  <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Summary
                  </h4>
                  <p className="text-sm">{req.summary}</p>
                </div>
              )}
              <ReqSection title="Must-Haves" items={req.mustHaves ?? []} />
              <ReqSection title="Nice-to-Haves" items={req.niceToHaves ?? []} />
              <ReqSection title="Responsibilities" items={req.responsibilities ?? []} />
              {(req.keywords ?? []).length > 0 && (
                <div>
                  <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Keywords
                  </h4>
                  <div className="flex flex-wrap gap-1.5">
                    {req.keywords!.map((kw) => (
                      <Badge key={kw} variant="secondary" className="text-xs">
                        {kw}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {hasReqs && (
            <button
              onClick={() => setExpanded((v) => !v)}
              className="mt-2 flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            >
              {expanded ? (
                <>
                  <ChevronUp className="h-3.5 w-3.5" /> Collapse
                </>
              ) : (
                <>
                  <ChevronDown className="h-3.5 w-3.5" /> View requirements
                </>
              )}
            </button>
          )}
        </CardContent>
      )}
    </Card>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────────

export default function PostingsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const [modalOpen, setModalOpen] = useState(false);
  const [modalInitialMode, setModalInitialMode] = useState<'url' | 'paste'>('url');
  const [modalInitialUrl, setModalInitialUrl] = useState('');
  const [appliedPosting, setAppliedPosting] = useState<JobPosting | null>(null);
  const [duplicateWarning, setDuplicateWarning] = useState<{
    id: string;
    title: string | null;
    hiringCompany: string | null;
    createdAt: string;
  } | null>(null);

  // Preserve duplicate warning from navigation state (set by modal onSuccess)
  const stateRef = useRef(location.state as { duplicate?: typeof duplicateWarning } | null);
  useEffect(() => {
    const s = stateRef.current;
    if (s?.duplicate) {
      setDuplicateWarning(s.duplicate);
      // Clear so it doesn't re-appear on refresh
      window.history.replaceState({}, '');
    }
  }, []);

  // ── Auth guard ─────────────────────────────────────────────────────────────

  const { data: authData, isLoading: authLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: authApi.me,
    retry: false,
  });

  if (!authLoading && !authData) {
    void navigate('/login');
    return null;
  }

  // ── Postings data ──────────────────────────────────────────────────────────

  const { data: postings = [], isLoading: postingsLoading } = useQuery({
    queryKey: ['job-postings'],
    queryFn: jobPostingApi.list,
    enabled: !!authData,
    // Poll every 3s when any posting is still processing
    refetchInterval: (query) => {
      const data = query.state.data as JobPosting[] | undefined;
      if (!data) return false;
      const hasPending = data.some(
        (p) => p.status === 'pending' || p.status === 'processing',
      );
      return hasPending ? 3000 : false;
    },
  });

  // ── Applications (for applied-status display on cards) ────────────────────

  const { data: applications = [] } = useQuery({
    queryKey: ['applications'],
    queryFn: applicationApi.list,
    enabled: !!authData,
  });

  // Most-recent application per posting (postingId → Application)
  const applicationByPosting = new Map<string, Application>();
  for (const app of applications) {
    if (!app.postingId) continue;
    const existing = applicationByPosting.get(app.postingId);
    if (!existing || app.createdAt > existing.createdAt) {
      applicationByPosting.set(app.postingId, app);
    }
  }

  // ── Credits balance ────────────────────────────────────────────────────────

  const { data: plan } = useQuery({
    queryKey: ['credits', 'balance'],
    queryFn: creditsApi.getBalance,
    enabled: !!authData,
    staleTime: 60_000,
  });

  // ── Mutations ──────────────────────────────────────────────────────────────

  const deleteMutation = useMutation({
    mutationFn: jobPostingApi.delete,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['job-postings'] });
    },
  });

  const generateResumeMutation = useMutation({
    mutationFn: (postingId: string) => resumeApi.create(postingId),
    onSuccess: (resume) => {
      void queryClient.invalidateQueries({ queryKey: ['resumes'] });
      void queryClient.invalidateQueries({ queryKey: ['credits', 'balance'] });
      void navigate(`/resumes/${resume.id}`);
    },
    onError: (error: any) => {
      if (error?.statusCode === 402) {
        showErrorToast('Not enough credits. An admin can grant you more.');
      }
    },
  });

  const synthesizeResumeMutation = useMutation({
    mutationFn: (postingId: string) => resumeApi.create(postingId, { strategy: 'synthesize' }),
    onSuccess: (resume) => {
      void queryClient.invalidateQueries({ queryKey: ['resumes'] });
      void queryClient.invalidateQueries({ queryKey: ['credits', 'balance'] });
      void navigate(`/resumes/${resume.id}`);
    },
    onError: (error: any) => {
      if (error?.statusCode === 402) {
        showErrorToast('Not enough credits. An admin can grant you more.');
      }
    },
  });

  const logoutMutation = useMutation({
    mutationFn: authApi.logout,
    onSuccess: () => { queryClient.clear(); void navigate('/login'); },
  });

  // ── Modal success handler ──────────────────────────────────────────────────

  const handleIngestSuccess = (result: IngestJobPostingResponse) => {
    if (result.duplicate) {
      setDuplicateWarning(result.duplicate);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  const pendingCount = postings.filter(
    (p) => p.status === 'pending' || p.status === 'processing',
  ).length;

  return (
    <div className="min-h-screen bg-background">
      {/* Persistent navigation */}
      <Navigation />

      {/* ── Content ────────────────────────────────────────────────────────── */}
      <main className="mx-auto max-w-4xl px-4 py-8">
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Job Postings</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Paste a job posting URL and we'll extract its requirements so you can generate a
              targeted resume.
            </p>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-2">
            {pendingCount > 0 && (
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" />
                {pendingCount} extracting…
              </span>
            )}
            <Button
              size="sm"
              onClick={() => setModalOpen(true)}
              className="gap-1"
            >
              <Plus className="h-4 w-4" />
              Add posting
            </Button>
          </div>
        </div>

        {/* Duplicate warning */}
        {duplicateWarning && (
          <div className="mb-4 flex items-start justify-between gap-3 rounded-md border border-yellow-300 bg-yellow-50 px-4 py-3 dark:border-yellow-800 dark:bg-yellow-950">
            <div className="flex items-start gap-2 text-sm text-yellow-800 dark:text-yellow-300">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                We found a similar existing posting
                {duplicateWarning.hiringCompany
                  ? ` from ${duplicateWarning.hiringCompany}`
                  : ''}{' '}
                added on {formatDate(duplicateWarning.createdAt)}.
                {duplicateWarning.title ? ` "${duplicateWarning.title}"` : ''}
              </span>
            </div>
            <button
              onClick={() => setDuplicateWarning(null)}
              className="shrink-0 text-yellow-600 hover:text-yellow-800"
            >
              ✕
            </button>
          </div>
        )}

        {/* Loading */}
        {postingsLoading && (
          <div className="flex items-center justify-center py-16 text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        )}

        {/* Empty state */}
        {!postingsLoading && postings.length === 0 && (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-16 text-center">
            <p className="text-muted-foreground">No job postings yet.</p>
            <Button
              variant="outline"
              size="sm"
              className="mt-4 gap-1"
              onClick={() => setModalOpen(true)}
            >
              <Plus className="h-4 w-4" />
              Add your first posting
            </Button>
          </div>
        )}

        {/* Posting cards */}
        {!postingsLoading && postings.length > 0 && (
          <div className="space-y-4">
            {postings.map((posting) => (
              <PostingCard
                key={posting.id}
                posting={posting}
                application={applicationByPosting.get(posting.id) ?? null}
                onDelete={(id) => deleteMutation.mutate(id)}
                onGenerate={(postingId) => generateResumeMutation.mutate(postingId)}
                onSynthesize={(postingId) => synthesizeResumeMutation.mutate(postingId)}
                onBuildManually={(postingId) => navigate(`/postings/${postingId}/build`)}
                onPasteDescription={(p) => {
                  setModalInitialMode('paste');
                  setModalInitialUrl(p.sourceUrl.startsWith(`${window.location.origin}/manual/`) ? '' : p.sourceUrl);
                  setModalOpen(true);
                }}
                onMarkApplied={(p) => setAppliedPosting(p)}
                creditsBalance={plan?.creditsBalance ?? 0}
              />
            ))}
          </div>
        )}
      </main>

      {/* ── Ingest modal ───────────────────────────────────────────────────── */}
      <JobPostingModal
        open={modalOpen}
        onOpenChange={(next) => {
          setModalOpen(next);
          if (!next) {
            setModalInitialMode('url');
            setModalInitialUrl('');
          }
        }}
        onSuccess={handleIngestSuccess}
        initialMode={modalInitialMode}
        initialUrl={modalInitialUrl}
      />

      {/* ── Mark as applied modal ───────────────────────────────────────────── */}
      {appliedPosting && (
        <MarkAsAppliedModal
          open={!!appliedPosting}
          onOpenChange={(next) => { if (!next) setAppliedPosting(null); }}
          postingId={appliedPosting.id}
          defaultCompany={appliedPosting.hiringCompany ?? appliedPosting.requirements?.role ?? null}
          defaultJobTitle={appliedPosting.title ?? appliedPosting.requirements?.role ?? null}
        />
      )}
    </div>
  );
}
